import { useState } from 'react';

const Concept = () => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 lg:p-8">
            {/* Header - Clean without emoji */}
            <div className="text-center mb-8">
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    Your Pronunciation Gym
                </h1>
                <div className="w-32 h-1 bg-gray-300 mx-auto mb-3"></div>
                <p className="text-gray-700 text-lg max-w-2xl mx-auto">
                    Systematic training for clear, natural speech
                </p>
            </div>

            {/* Problem Statement - Direct & Punchy */}
            <div className="mb-10">
                <div className="flex items-center justify-center mb-6">
                    <div className="text-3xl mr-3">❓</div>
                    <h2 className="text-2xl font-bold text-gray-900">You know the words, but...</h2>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200 max-w-2xl mx-auto">
                    <div className="space-y-4 mb-6">
                        <div className="flex items-start">
                            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0 mt-0.5">•</div>
                            <div className="text-lg text-gray-800 pt-0.5">
                                Native speakers may ask you to repeat
                            </div>
                        </div>
                        
                        <div className="flex items-start">
                            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg mr-4 flex-shrink-0 mt-0.5">•</div>
                            <div className="text-lg text-gray-800 pt-0.5">
                                Real conversations often move faster than you can process
                            </div>
                        </div>
                    </div>
                    
                    {/* The core insight */}
                    <div className="mt-6 pt-6 border-t border-blue-300/50">
                        <div className="flex items-center">
                            <div className="text-2xl mr-3">🎯</div>
                            <div>
                                <div className="text-lg font-bold text-gray-900 mb-1">
                                    Pronunciation is a physical skill
                                </div>
                                <div className="text-gray-700">
                                    Traditional training neglects the physical and auditory skills required for clear, natural speech.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Core Idea */}
            <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                    Pronunciation as a Trainable Skill
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                        <div className="flex items-center mb-3">
                            <div className="text-2xl mr-3">💪</div>
                            <h3 className="text-lg font-bold text-gray-900">Like Athletic Training</h3>
                        </div>
                        <p className="text-gray-700">
                            Structured exercises for articulatory precision and vocal control
                        </p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                        <div className="flex items-center mb-3">
                            <div className="text-2xl mr-3">🎵</div>
                            <h3 className="text-lg font-bold text-gray-900">Like Musical Training</h3>
                        </div>
                        <p className="text-gray-700">
                            Ear training for rhythm, stress, and intonation patterns
                        </p>
                    </div>
                </div>
                
                <div className="text-center text-gray-700">
                    <p className="font-medium">
                        Suitable for learners of all levels who want to improve speech clarity and fluency.
                    </p>
                </div>
            </div>

            {/* Three Key Elements */}
            <div className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Three Essential Skills</h2>
                
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="text-center">
                        <div className="text-3xl mb-3">👄</div>
                        <h4 className="font-bold text-gray-900 mb-2">Articulation Precision</h4>
                        <p className="text-sm text-gray-700">
                            Train the vocal tract to produce unfamiliar sounds accurately
                        </p>
                    </div>
                    
                    <div className="text-center">
                        <div className="text-3xl mb-3">↗️</div>
                        <h4 className="font-bold text-gray-900 mb-2">Prosody Mastery</h4>
                        <p className="text-sm text-gray-700">
                            Learn the rhythm, stress, and intonation patterns of natural speech
                        </p>
                    </div>
                    
                    <div className="text-center">
                        <div className="text-3xl mb-3">👂</div>
                        <h4 className="font-bold text-gray-900 mb-2">Auditory Discrimination</h4>
                        <p className="text-sm text-gray-700">
                            Develop auditory discrimination for subtle sound differences
                        </p>
                    </div>
                </div>
            </div>

            {/* Methodology Preview */}
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Two Practice Modes</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Shadowing</h3>
                        <p className="text-gray-700 mb-3">
                            Match native speakers' rhythm and intonation in real time
                        </p>
                        <div className="text-sm text-green-700 font-medium">
                            Focus: speech flow and timing
                        </div>
                    </div>
                    
                    <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                        <h3 className="text-lg font-bold text-gray-900 mb-2">Echoing</h3>
                        <p className="text-gray-700 mb-3">
                            Pause and reproduce specific sounds or short phrases with precision
                        </p>
                        <div className="text-sm text-purple-700 font-medium">
                            Focus: articulatory accuracy and control
                        </div>
                    </div>
                </div>
            </div>

            {/* Expandable Section */}
            {isExpanded && (
                <div className="mt-10 pt-8 border-t border-gray-200 space-y-8">
                    {/* Detailed Methodology */}
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4">How It Works</h3>
                        <div className="space-y-6">
                            <div className="bg-gray-50 rounded-xl p-5">
                                <h4 className="font-bold text-gray-900 mb-3">Shadowing vs Echoing</h4>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <h5 className="font-semibold text-gray-800 mb-2">Shadowing</h5>
                                        <ul className="text-gray-700 space-y-1 text-sm">
                                            <li>• Continuous practice focusing on rhythm</li>
                                            <li>• Develops prosodic awareness and speech fluency</li>
                                            <li>• Develops speech flow and timing</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h5 className="font-semibold text-gray-800 mb-2">Echoing</h5>
                                        <ul className="text-gray-700 space-y-1 text-sm">
                                            <li>• Discrete practice focusing on accuracy</li>
                                            <li>• Develops control over individual sounds</li>
                                            <li>• Develops precise articulation</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="grid md:grid-cols-3 gap-4">
                                <div className="text-center">
                                    <div className="text-2xl mb-2">🎯</div>
                                    <h5 className="font-semibold text-gray-900 mb-1">Targeted Feedback</h5>
                                    <p className="text-sm text-gray-700">
                                        Visual analysis of phoneme accuracy and intonation patterns
                                    </p>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl mb-2">📊</div>
                                    <h5 className="font-semibold text-gray-900 mb-1">Progress Tracking</h5>
                                    <p className="text-sm text-gray-700">
                                        Monitor development across sound categories and prosodic features
                                    </p>
                                </div>
                                <div className="text-center">
                                    <div className="text-2xl mb-2">🎙️</div>
                                    <h5 className="font-semibold text-gray-900 mb-1">Native Models</h5>
                                    <p className="text-sm text-gray-700">
                                        Practice with recordings from multiple native speakers
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* For Serious Learners */}
                    <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                        <h4 className="font-bold text-gray-900 mb-3">Research-Informed Approach</h4>
                        <p className="text-gray-700 mb-3">
                            The methodology addresses both segmental (individual sounds) and suprasegmental (prosodic) features of speech, adapted to different language backgrounds.
                        </p>
                        <div className="text-sm text-gray-600">
                            Designed for learners who seek systematic, measurable improvement in comprehensibility and naturalness.
                        </div>
                    </div>
                </div>
            )}

            {/* Expand/Collapse Button */}
            <div className="text-center mt-8">
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="inline-flex items-center px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                    {isExpanded ? (
                        <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            Show Less
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            Show Details
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default Concept;